// How often to look at a chat. Pure, so it is tested without a browser.
//
// Every look is two calls to Anthropic and a read of the person's chats, and a
// build can be open in a tab for the better part of an hour. So: look often
// while things are moving, less when nothing has changed, rarely when nobody is
// waiting on it, and not at all in a tab nobody can see.

export const BUSY_MS = 3000;
export const BUSY_MAX_MS = 15000;
export const IDLE_MS = 20000;

/** Milliseconds until the next look, or `null` to wait until the tab is shown.
 *
 *  `working`: the builder owes a reply. `unchanged`: how many looks in a row
 *  found nothing new. A change, or the person sending something, sets it back to
 *  zero, so a reply is seen within three seconds of it starting. */
export function pollDelay({ working, hidden, unchanged }) {
  if (hidden) return null;
  if (!working) return IDLE_MS;
  return Math.min(BUSY_MAX_MS, BUSY_MS * 2 ** Math.min(Math.max(unchanged, 0), 3));
}

/** The builder owes a reply: it is running, or the last word is the person's
 *  and the session has not ended. A session reads "idle" for the seconds
 *  between a message landing and the agent picking it up, so status alone
 *  left the first message with no sign anything was happening. */
export function awaitingReply(view) {
  if (!view) return false;
  if (view.status === "running" || view.status === "rescheduling") return true;
  const last = (view.messages || [])[(view.messages || []).length - 1];
  return last?.role === "user" && view.status !== "terminated";
}

/** What a look found, small enough to compare. Two looks with the same
 *  fingerprint saw nothing new. */
export function fingerprint(view) {
  if (!view) return "";
  return [view.status, view.stage, view.decision || "", (view.messages || []).length,
    view.cost?.cents ?? 0, view.archived ? 1 : 0].join("|");
}

// How often to look at a sandbox (LO-187).
//
// A sandbox goes through three waits of very different length: the instance is
// built (ten minutes or so), its data is written (seconds), and a QA run
// explores it (minutes). Each look is three calls to the kernel and the control
// plane's table, so the interval is per wait, backs off while nothing changes,
// and the panel stops looking altogether once nothing will change by itself.

/** [first, longest] milliseconds between looks, per kind of wait. */
export const SANDBOX_WAIT_MS = {
  provisioning: [10000, 30000],
  seeding: [5000, 15000],
  qa: [5000, 20000],
  // Ready and no run yet, or no sandbox yet: one is about to appear.
  waiting: [5000, 20000],
  stale: [30000, 30000],
  unknown: [15000, 30000],
};

/** Looks in a row that found nothing new, after which the panel stops and says
 *  so. At the slowest interval this is well over ten minutes. */
export const SANDBOX_GIVE_UP = 40;

/** Milliseconds until the next look at a sandbox, or `null` to stop until the
 *  tab is shown or somebody presses Check again.
 *
 *  `phase` is from `phaseOf`; `done` and a hidden tab are not looked at. */
export function sandboxPollDelay({ phase, hidden, unchanged }) {
  if (hidden || phase === "done") return null;
  const wait = SANDBOX_WAIT_MS[phase] || SANDBOX_WAIT_MS.waiting;
  const quiet = Math.max(unchanged, 0);
  if (quiet >= SANDBOX_GIVE_UP) return null;
  return Math.min(wait[1], wait[0] * 2 ** Math.min(quiet, 5));
}
